import React, {useState, useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {useHistory, useLocation} from 'react-router-dom';
import moment from 'moment-timezone';
import queryString from 'query-string';
import useDeepCompareEffect from 'use-deep-compare-effect';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import {DataGridPro, GridActionsCellItem} from '@mui/x-data-grid-pro';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';

import PrimaryAppBar from './PrimaryAppBar';
import SecondaryAppBar from './SecondayAppBar';
import ExerciseMenu from './ExerciseMenu';
import InstructionsPreviewDialog from './InstructionsPreviewDialog';
import CopyExerciseDialog from './CopyExerciseDialog';
import VisibilityChip from './VisibilityChip';
import DataGridQuickSearchToolbar from './DataGridQuickSearchToolbar';
import {fetchExercises, selectExercises, selectIsLoading as selectIsBuilderLoading} from '../../redux/slices/builder';
import {
  selectUserGroup,
  selectUserGroups,
  fetchUserGroup,
  fetchUsers,
  selectUser,
  selectUsers,
  selectIsLoading as selectIsUserLoading,
} from '../../redux/slices/user';
import {faPlay, faAlignLeft, faEllipsisHAlt} from '@fortawesome/pro-light-svg-icons';
import {faPlus} from '@fortawesome/pro-regular-svg-icons';
import ImageNoData from '../images/no-data.svg';
import {APP_READING, APP_TITLES, APP_WRITING, FEATURES} from '../../constants';

import styles from '../css/Exercises.module.css';
import baseStyles from '../../css/Base.module.css';

export default function Exercises() {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  const [isMenuOpen, toggleMenu] = useState(false);
  const [activeExercise, setActiveExercise] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isCopyExerciseDialogOpen, toggleCopyExerciseDialog] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState(null);
  const [exercisesClone, setExercisesClone] = useState([]); // TODO: Refactor this away
  const [searchText, setSearchText] = useState('');
  const [dataGridToolbarFilterButtonEl, setDataGridToolbarFilterButtonEl] = useState(false);
  const [isInstructionsPreviewDialogOpen, setIsInstructionsPreviewDialogOpen] = useState(false);

  const userGroup = useSelector(selectUserGroup);
  const userGroups = useSelector(selectUserGroups);
  const user = useSelector(selectUser);
  const users = useSelector(selectUsers);
  const isBuilderLoading = useSelector(selectIsBuilderLoading);
  const isUserLoading = useSelector(selectIsUserLoading);
  const allExercises = useSelector(selectExercises);

  const queryStrings = queryString.parse(location.search);
  const exerciseType = queryStrings.type;
  const {
    tier: {features},
  } = userGroup;
  const isContentProducerGroup = features.includes(FEATURES.VISIBILITY_PUBLIC);
  const isLoading = isBuilderLoading || isUserLoading;

  const myExercisesFilter = (exercise) => exercise.type === exerciseType && exercise.userID === user._id;
  const sharedWithMeFilter = (exercise) =>
    exercise.type === exerciseType && exercise.userID !== user._id && exercise.visibility === 'USER_GROUP';
  const flamingoFilter = (exercise) => exercise.type === exerciseType && exercise.visibility === 'PUBLIC';
  let filterFn = myExercisesFilter;
  if (activeTabIndex === 1) filterFn = sharedWithMeFilter;
  else if (activeTabIndex === 2) filterFn = flamingoFilter;
  const exercises = allExercises.filter(filterFn).map((exercise) => ({id: exercise._id, ...exercise})); // DataGrid requires unique id property

  // Quick search toolbar BEGIN
  function escapeRegExp(value) {
    return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
  }

  const requestSearch = (searchValue) => {
    setSearchText(searchValue);
    const searchRegex = new RegExp(escapeRegExp(searchValue), 'i');

    const filteredRows = exercises.filter((row) => {
      const topLevelIgnoreFields = ['_id', 'id', 'userGroupID', 'userID', '__v', 'type', 'config'];
      let fieldsToSearch = Object.keys(row).filter((field) => !topLevelIgnoreFields.includes(field));
      const configFields = row?.config ? Object.keys(row.config).map((field) => `config.${field}`) : [];
      fieldsToSearch = fieldsToSearch.concat(configFields);

      return fieldsToSearch.some((field) => {
        let searchValue;
        if (field === 'tags') searchValue = row[field].map((tag) => tag.name).join(',');
        else if (field === 'visibility') searchValue = row[field] === 'USER_GROUP' ? 'Everyone' : row[field];
        else if (field.startsWith('config.') && field !== 'config.sentenceHTML')
          searchValue = row.config[[field.replace('config.', '')]]?.toString() ?? '';
        else searchValue = row[field]?.toString() ?? '';

        return searchRegex.test(searchValue);
      });
    });
    setExercisesClone(filteredRows);
  };
  // Quick search toolbar END

  // Changing order can affect columns below!
  // The remove prop is not a DataGrid / Grid column property
  const DEFAULT_COLUMNS = [
    {
      field: 'action',
      type: 'actions',
      hideable: false,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<FontAwesomeIcon icon={faPlay} className="clr-dark-blue" />}
          onClick={(event) => handleClickPreviewExercise(event, params.row)}
          label="Preview"
        />,
        <GridActionsCellItem
          icon={<FontAwesomeIcon icon={faAlignLeft} className="clr-dark-blue" />}
          onClick={(event) => handleClickPreviewInstructions(event, params.row)}
          label="Instructions"
        />,
        <GridActionsCellItem
          icon={<FontAwesomeIcon icon={faEllipsisHAlt} className="clr-dark-blue" />}
          onClick={(event) => handleClickMenu(event, params.row)}
          label="Menu"
        />,
      ],
    },
    {field: 'name', headerName: 'Name', width: 150},
    {field: 'description', headerName: 'Description', width: 250},
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 150,
      type: 'dateTime',
      valueFormatter: (params) => moment(params.value).format('M/D/YYYY h:mm a'),
    },
    {
      field: 'by',
      headerName: 'By',
      remove: activeTabIndex === 0,
      width: 150,
      renderCell: (params) => {
        const exerciseUser = users[params.row.userID];
        let name;
        let imageURL;

        if ([0, 1].includes(activeTabIndex) || (activeTabIndex === 2 && isContentProducerGroup)) {
          name = `${exerciseUser?.firstName ?? ''} ${exerciseUser?.lastName ?? ''}`.trim();
          imageURL = exerciseUser?.avatarURL;
        } else if (activeTabIndex === 2) {
          const exerciseUserGroup = userGroups[params.row.userGroupID];
          name = exerciseUserGroup.name;
          imageURL = exerciseUserGroup?.logoURL;
        }
        const initial = name ? name.charAt(0) : null;

        return (
          <>
            <Avatar src={imageURL} style={{display: 'inline-flex', width: 24, height: 24}}>
              {initial ?? <PersonOutlineOutlinedIcon />}
            </Avatar>
            <span style={{marginLeft: 3, verticalAlign: 'top'}}>{name}</span>
          </>
        );
      },
    },
    {
      field: 'tags',
      headerName: 'Tags',
      width: 300,
      valueGetter: (params) => params.row.tags.map((tag) => tag.name).join(','),
      renderCell: (params) => {
        const {tags} = params.row;
        return (
          <Stack direction="row" spacing={1} sx={{overflowX: 'auto'}}>
            {tags.map((tag) => (
              <Chip key={tag._id} label={tag.name} />
            ))}
          </Stack>
        );
      },
    },
    {
      field: 'visibility',
      headerName: 'Visibility',
      remove: activeTabIndex === 2,
      valueGetter: (params) => (params.row.visibility === 'USER_GROUP' ? 'Everyone' : params.row.visibility),
      renderCell: (params) => <VisibilityChip visibility={params.row.visibility} />,
    },
  ].filter((column) => !column.remove);

  const [defaultColumnsAction, defaultColumnsName, defaultColumnsDescription, ...defaultColumnRest] = DEFAULT_COLUMNS;
  const READING_COLUMNS = [
    defaultColumnsAction,
    {...defaultColumnsName, headerName: 'Book'},
    defaultColumnsDescription,
    {field: 'genre', headerName: 'Genre', width: 85, valueGetter: (params) => params.row.config.genre},
    {field: 'language', headerName: 'Language', width: 75, valueGetter: (params) => params.row.config.language},
    {field: 'levelEIL', headerName: 'EIL', width: 50, valueGetter: (params) => params.row.config.levelEIL},
    {field: 'levelFP', headerName: 'F&P', width: 50, valueGetter: (params) => params.row.config.levelFP},
    {field: 'levelLexile', headerName: 'Lexile', width: 55, valueGetter: (params) => params.row.config.levelLexile},
    {
      field: 'wordCount',
      headerName: 'Words',
      type: 'number',
      width: 55,
      valueGetter: (params) => params.row.config.wordCount,
    },
    ...defaultColumnRest,
  ];
  const WRITING_COLUMNS = [
    defaultColumnsAction,
    {
      field: 'sentence',
      headerName: 'Sentence',
      width: 300,
      valueGetter: (params) => params.row.config.sentencePlainText,
      renderCell: (params) => <span dangerouslySetInnerHTML={{__html: params.row.config.sentenceHTML}} />,
    },
    {...defaultColumnsName, headerName: 'Book'},
    ...defaultColumnRest,
  ];

  useEffect(() => {
    if (isContentProducerGroup) setActiveTabIndex(2);
    else setActiveTabIndex(0);
  }, [isContentProducerGroup]);

  useEffect(() => {
    setSearchText('');
    setExercisesClone([]);
    if (activeTabIndex === 0) dispatch(fetchExercises({userID: user._id, type: exerciseType}));
    else if (activeTabIndex === 1) dispatch(fetchExercises({type: exerciseType, visibility: 'USER_GROUP'}));
    else if (activeTabIndex === 2) {
      const userGroupIDs = isContentProducerGroup ? [userGroup._id] : Object.keys(userGroup.tier.content);
      userGroupIDs.forEach((userGroupID) =>
        dispatch(fetchExercises({userGroupID, type: exerciseType, visibility: 'PUBLIC'})),
      );
    }
  }, [dispatch, activeTabIndex, exerciseType, user._id]); // eslint-disable-line react-hooks/exhaustive-deps

  useDeepCompareEffect(() => {
    fetchMissingLogoEntities();
  }, [exercises]);

  useDeepCompareEffect(() => {
    if (hasAllLogoEntities()) {
      setExercisesClone(exercises);
    }
  }, [exercises, users, userGroups, isBuilderLoading]);

  function hasAllLogoEntities() {
    if ([0, 1].includes(activeTabIndex) || (activeTabIndex === 2 && isContentProducerGroup)) {
      const userIDs = Array.from(new Set(exercises.map((exercise) => exercise.userID)));
      return userIDs.every((userID) => Object.keys(users).includes(userID));
    } else if (activeTabIndex === 2) {
      const userGroupIDs = Object.keys(userGroup.tier.content);
      return userGroupIDs.every((userGroupID) => Object.keys(userGroups).includes(userGroupID));
    }
  }

  function fetchMissingLogoEntities() {
    if (exercises.length > 0) {
      if ([0, 1].includes(activeTabIndex) || (activeTabIndex === 2 && isContentProducerGroup)) {
        const userIDs = Array.from(new Set(exercises.map((exercise) => exercise.userID))).filter(
          (userID) => !Object.keys(users).includes(userID),
        );
        if (userIDs.length > 0) dispatch(fetchUsers(userIDs));
      } else if (activeTabIndex === 2) {
        const userGroupIDs = Object.keys(userGroup.tier.content);
        userGroupIDs.forEach((userGroupID) => dispatch(fetchUserGroup(userGroupID)));
      }
    }
  }

  function handleClickMenu(event, exercise) {
    setActiveExercise(exercise);
    setAnchorEl(event.currentTarget);
    toggleMenu(true);
    event.stopPropagation();
  }

  function handleClickPreviewExercise(event, exercise) {
    const {_id: exerciseID} = exercise;
    window.open(`/exercise/${exerciseID}`);
  }

  function handleClickPreviewInstructions(event, exercise) {
    setActiveExercise(exercise);
    setIsInstructionsPreviewDialogOpen(true);
    event.stopPropagation();
  }

  function handleChangeTab(event, newTabIndex) {
    setActiveTabIndex(newTabIndex);
  }

  function navigateToCreate() {
    history.push(`/builder/exercise/create?type=${exerciseType}`);
  }

  let columns = DEFAULT_COLUMNS;
  if (exerciseType === APP_READING) columns = READING_COLUMNS;
  else if (exerciseType === APP_WRITING) columns = WRITING_COLUMNS;

  return (
    <>
      <InstructionsPreviewDialog
        isOpen={isInstructionsPreviewDialogOpen}
        setIsOpen={setIsInstructionsPreviewDialogOpen}
        instructionsHTML={activeExercise?.instructions?.html}
      />

      <PrimaryAppBar />
      <SecondaryAppBar title={APP_TITLES[exerciseType]}>
        <IconButton
          onClick={navigateToCreate}
          className="bg-success"
          size="large"
          sx={{'&:hover': {backgroundColor: 'rgba(66, 184, 94, 0.8)'}}}
        >
          <FontAwesomeIcon icon={faPlus} className="clr-gray-200" size="xs" />
        </IconButton>
      </SecondaryAppBar>

      {Number.isInteger(activeTabIndex) && !isContentProducerGroup && (
        <Tabs
          className="m-b-30"
          value={activeTabIndex}
          onChange={handleChangeTab}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="MINE" />
          <Tab label="SHARED WITH ME" />
          <Tab label="FLAMINGO" />
        </Tabs>
      )}

      <CopyExerciseDialog
        isOpen={isCopyExerciseDialogOpen}
        handleClose={() => toggleCopyExerciseDialog(false)}
        exercise={activeExercise}
        setActiveTabIndex={setActiveTabIndex}
      />

      <ExerciseMenu
        anchorEl={anchorEl}
        isMenuOpen={isMenuOpen}
        toggleMenu={toggleMenu}
        exercise={activeExercise}
        toggleCopyExerciseDialog={toggleCopyExerciseDialog}
      />

      {isLoading && (
        <div className={baseStyles['progress-container']}>
          <CircularProgress />
        </div>
      )}

      {!isLoading && exercises.length === 0 && (
        <div className={styles.empty}>
          <img src={ImageNoData} alt="No Exercises" className={'m-t-30 m-b-30'} />
          {activeTabIndex === 0 && (
            <Button className={baseStyles['btn-pill']} variant="outlined" onClick={navigateToCreate}>
              NEW EXERCISE
            </Button>
          )}
        </div>
      )}

      {!isLoading && exercises?.length > 0 && (
        <div style={{height: 'calc(100vh - 195px)', width: '100%'}}>
          <div style={{display: 'flex', height: '100%'}}>
            <div style={{flexGrow: 1}}>
              <DataGridPro
                pagination
                components={{Toolbar: DataGridQuickSearchToolbar}}
                componentsProps={{
                  toolbar: {
                    value: searchText,
                    onChange: (event) => requestSearch(event.target.value),
                    clearSearch: () => requestSearch(''),
                    setFilterButtonEl: setDataGridToolbarFilterButtonEl,
                  },
                  panel: {
                    anchorEl: dataGridToolbarFilterButtonEl,
                  },
                }}
                rows={exercisesClone}
                columns={columns}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
